// add "use client" directive
'use client'

import { FC, Fragment, useEffect, useRef, useState } from 'react'
import tw from 'twin.macro'
import { Link } from 'gatsby'
import { Grid } from './UI'
import { Menu, Transition } from '@headlessui/react'
import { observer } from 'mobx-react-lite'

import logo from '../images/gabbi-logo-white-r.png'
import FeatherIcon from './FeatherIcon'
import { useLocation } from '@reach/router'

type Props = {
  headerColor?: 'clear' | 'red' | 'blue' | 'cream'
  headerHoverColor?: 'red' | 'blue' | 'black'
}

const usePopoverHover = () => {
  const popoverRef = useRef<HTMLElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const popoverRef2 = useRef<HTMLElement>(null)
  const buttonRef2 = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const popover = popoverRef.current
    const button = buttonRef.current
    const popover2 = popoverRef2.current
    const button2 = buttonRef2.current

    if (!popover || !button || !popover2 || !button2) {
      console.warn(
        'usePopoverHover: Please assign popoverRef and buttonRef for this to function correctly.'
      )
      return () => {}
    }

    const clickButton = () => {
      button.click()
    }
    const clickButton2 = () => {
      button2.click()
    }

    popover.addEventListener('mouseenter', clickButton)
    popover.addEventListener('mouseleave', clickButton)
    popover2.addEventListener('mouseenter', clickButton2)
    popover2.addEventListener('mouseleave', clickButton2)

    return () => {
      popover.removeEventListener('mouseenter', clickButton)
      popover.removeEventListener('mouseleave', clickButton)
      popover2.removeEventListener('mouseenter', clickButton2)
      popover2.removeEventListener('mouseleave', clickButton2)
    }
  }, [])

  return { popoverRef, popoverRef2, buttonRef, buttonRef2 }
}

const Header: FC<Props> = ({ headerColor, headerHoverColor = 'red' }) => {
  const [expanded, setExpanded] = useState(false)
  const { popoverRef, buttonRef, popoverRef2, buttonRef2 } = usePopoverHover()
  const [signInButton, setSignInButton] = useState(true)
  const [headerBGColor, setheaderBGColor] = useState(headerColor || 'clear')

  const aboutLinks = [
    { href: '/#about', label: 'Our Story' },
    { href: '/#providers', label: 'Our Providers' },
    { href: '/#press', label: 'In the News' },
    { href: '/contact/', label: 'Contact Us' },
  ]

  const partnerShipLinks = [
    { href: '/health-systems/', label: 'For Health Systems' },
    { href: '/employers/', label: 'For Employers' },
    { href: '/providers/', label: 'For Providers' },
  ]

  const toggleMobileNav = () => {
    setExpanded(!expanded)
  }

  const location = useLocation()

  useEffect(() => {
    document.getElementsByTagName('body')[0].style.overflow = expanded
      ? 'hidden'
      : 'visible'
  }, [expanded])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', () => {
        if (window.pageYOffset >= 200) {
          setheaderBGColor(headerColor ? headerColor : 'blue')
        } else if (window.pageYOffset < 200 && !headerColor) {
          setheaderBGColor('clear')
        }
      })
    }
  }, [])

  useEffect(() => {
    console.log(JSON.stringify(location))
    switch (location.pathname) {
      case '/health-systems/':
      case '/employers/':
      case '/providers/':
        setSignInButton(false)
        break
      default:
        setSignInButton(true)
    }
    console.log(signInButton)
  }, [location])

  return (
    <section
      css={[
        tw`sticky top-0 left-0 w-full z-30 mb-[-100px] transition duration-500`,
        headerBGColor === 'clear' &&
          !expanded &&
          tw`bg-transparent hover:(bg-gb-blue)`,
        headerBGColor === 'red' && tw`bg-gb-red`,
        headerBGColor === 'blue' && tw`bg-gb-blue`,
        headerBGColor === 'cream' && tw`bg-gb-cream`,
        expanded &&
          tw`fixed bg-gb-blue h-screen lg:(block bg-transparent h-auto)`,
      ]}
    >
      {/* Grid */}
      <Grid tw="h-[80px] lg:(h-[100px])">
        {/* Logo */}
        <div tw="col-span-2 flex items-center">
          <Link to="/">
            <img src={logo} tw="w-[148px] lg:w-[163px]" alt="" />
          </Link>
        </div>

        {/* Desktop links */}
        <div tw="hidden lg:(col-start-4 col-span-9 flex items-center justify-end font-domaine text-[19px])">
          <ul
            css={[
              tw`flex justify-between h-[100%] items-center`,
              headerBGColor === 'clear' && tw`text-white`,
              headerBGColor === 'red' && tw`text-white`,
              headerBGColor === 'blue' && tw`text-white`,
              headerBGColor === 'cream' && tw`text-black`,
            ]}
          >
            <li tw="text-center inline h-[100%] line-height[100%]">
              <Menu
                as="div"
                ref={popoverRef}
                tw="relative inline-block text-left h-[100%]"
              >
                {({ open }) => (
                  <Fragment>
                    <div
                      css={[
                        tw`px-[20px] py-[10px] inline-block h-[100%] line-height[100%] font-domaine  focus:(outline-none) hover:( text-gb-red)`,
                        headerHoverColor === 'blue' && tw`hover:(text-gb-blue)`,
                        headerBGColor === 'red' &&
                          tw`hover:(text-gb-blue-dark)`,
                      ]}
                    >
                      {/* <Menu.Button>Sub Menu</Menu.Button> */}
                      <Menu.Button
                        ref={buttonRef}
                        css={[
                          tw`h-[100%] line-height[100%] focus:(outline-none) focus-visible:(outline-none)`,
                        ]}
                      >
                        About
                      </Menu.Button>
                    </div>
                    {/* Use the `Transition` component. */}
                    <Transition
                      enter="transition duration-600 ease-out"
                      enterFrom="transform scale-95 opacity-0"
                      enterTo="transform scale-100 opacity-100"
                      leave="transition duration-300 ease-out"
                      leaveFrom="transform scale-100 opacity-100"
                      leaveTo="transform scale-95 opacity-0"
                    >
                      <Menu.Items static>
                        <div
                          tw="absolute"
                          css={[
                            tw`absolute w-[250px] py-[20px] border-t-4 border-t-gb-red`,
                            headerBGColor === 'red' && tw`hover:(bg-gb-red)`,
                            headerBGColor === 'blue' && tw`hover:(bg-gb-blue)`,
                            headerBGColor === 'cream' &&
                              tw`hover:(bg-gb-cream)`,
                            open && headerBGColor != 'clear' && tw`bg-gb-blue`,
                            open && headerBGColor === 'clear' && tw`bg-gb-blue`,
                          ]}
                        >
                          {aboutLinks.map((link) => (
                            /* Use the `active` state to conditionally style the active item. */
                            <Menu.Item
                              key={link.href}
                              css={[
                                tw`block px-[20px] py-[10px] text-left font-domaine hover:(text-gb-red)`,
                                headerHoverColor === 'blue' &&
                                  tw`hover:(text-gb-blue)`,
                                headerBGColor === 'red' &&
                                  tw`hover:(text-gb-blue-dark)`,
                              ]}
                            >
                              <a href={link.href}>{link.label}</a>
                            </Menu.Item>
                          ))}
                        </div>
                      </Menu.Items>
                    </Transition>
                  </Fragment>
                )}
              </Menu>
            </li>
            <li tw="text-center inline h-[100%] line-height[100%]">
              <Menu
                as="div"
                ref={popoverRef2}
                tw="relative inline-block text-left h-[100%]"
              >
                {({ open }) => (
                  <Fragment>
                    <div
                      css={[
                        tw`px-[20px] py-[10px] inline-block h-[100%] line-height[100%] font-domaine hover:( text-gb-red)`,
                        headerHoverColor === 'blue' && tw`hover:(text-gb-blue)`,
                        headerBGColor === 'red' &&
                          tw`hover:(text-gb-blue-dark)`,
                      ]}
                    >
                      {/* <Menu.Button>Sub Menu</Menu.Button> */}
                      <Menu.Button
                        ref={buttonRef2}
                        css={[
                          tw`h-[100%] line-height[100%] focus:(outline-none) focus-visible:(outline-none)`,
                        ]}
                      >
                        Partnerships
                      </Menu.Button>
                    </div>
                    {/* Use the `Transition` component. */}
                    <Transition
                      as={Fragment}
                      enter="transition duration-600 ease-out"
                      enterFrom="transform scale-95 opacity-0"
                      enterTo="transform scale-100 opacity-100"
                      leave="transition duration-300 ease-out"
                      leaveFrom="transform scale-100 opacity-100"
                      leaveTo="transform scale-95 opacity-0"
                      appear={true}
                      show={open}
                    >
                      <Menu.Items static>
                        <div
                          css={[
                            tw`absolute w-[250px] py-[20px] border-t-4 border-t-gb-red`,
                            headerBGColor === 'red' && tw`hover:(bg-gb-red)`,
                            headerBGColor === 'blue' && tw`hover:(bg-gb-blue)`,
                            headerBGColor === 'cream' &&
                              tw`hover:(bg-gb-cream)`,
                            open && headerBGColor != 'clear' && tw`bg-gb-blue`,
                            open && headerBGColor === 'clear' && tw`bg-gb-blue`,
                          ]}
                        >
                          {partnerShipLinks.map((link) => (
                            /* Use the `active` state to conditionally style the active item. */
                            <Menu.Item
                              key={link.href}
                              css={[
                                tw`block px-[20px] py-[10px] text-left font-domaine hover:(text-gb-red)`,
                                headerHoverColor === 'blue' &&
                                  tw`hover:(text-gb-blue)`,
                                headerBGColor === 'red' &&
                                  tw`hover:(text-gb-blue-dark)`,
                              ]}
                            >
                              <a href={link.href}>{link.label}</a>
                            </Menu.Item>
                          ))}
                        </div>
                      </Menu.Items>
                    </Transition>
                  </Fragment>
                )}
              </Menu>
            </li>
            <li tw="text-center inline">
              <Link
                to="/individuals"
                css={[
                  tw`mx-[20px] py-[10px] inline-block line-height[1] font-domaine hover:(text-gb-red)`,
                  headerHoverColor === 'blue' && tw`hover:(text-gb-blue)`,
                  headerHoverColor === 'red' && tw`hover:(text-gb-red)`,
                ]}
              >
                For Individuals
              </Link>
            </li>
            {signInButton && (
              <Fragment>
                <li tw="text-center inline">
                  <Link
                    to="https://app.gabbi.com/login"
                    css={[
                      tw`mx-[20px] py-[10px] inline-block line-height[1] font-domaine hover:(text-gb-red)`,
                      headerHoverColor === 'blue' && tw`hover:(text-gb-blue)`,
                      headerHoverColor === 'red' && tw`hover:(text-gb-red)`,
                    ]}
                  >
                    Sign&nbsp;in
                  </Link>
                </li>
                <li tw="text-center inline">
                  <a
                    css={[
                      tw`ml-[20px] my-[5px] px-[10px] inline-block font-object border rounded hover:(text-gb-red border-gb-red)`,
                      headerHoverColor === 'blue' &&
                        tw`hover:(text-gb-blue border-gb-blue)`,
                    ]}
                    href="https://app.gabbi.com/intro-questions/interception"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Learn your risk
                  </a>
                </li>
              </Fragment>
            )}
            {signInButton === false && (
              <li tw="text-center inline">
                <a
                  css={[
                    tw`ml-[20px] my-[5px] w-[160px] inline-block font-object border rounded hover:(text-gb-red border-gb-red)`,
                    headerHoverColor === 'blue' &&
                      tw`hover:(text-gb-blue border-gb-blue)`,
                  ]}
                  href="https://share.hsforms.com/2TWXuPUcTQiGU1hEtw9nNUAnkzhj?tid=3728a85c-a485-48d2-938b-8f004b7be786"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Partner with us
                </a>
              </li>
            )}
          </ul>
        </div>

        {/* Hamburger */}
        <div tw="col-span-2 flex justify-end items-center lg:(hidden)">
          <div tw="mr-4 lg:hidden flex items-center">
            {!expanded && (
              <button onClick={toggleMobileNav}>
                <hr tw="w-7 mb-2 border-white" />
                <hr tw="w-7 my-2 border-white" />
                <hr tw="w-7 mt-2 border-white" />
              </button>
            )}

            {expanded && (
              <button onClick={toggleMobileNav} tw="h-7">
                <hr css={[tw`w-7 border-white rotate-[45deg]`]} />
                <hr css={[tw`w-7 border-white rotate-[-45deg]`]} />
              </button>
            )}
          </div>
        </div>
      </Grid>

      {/* Hamburger Content */}
      <Grid css={[tw`hidden lg:hidden`, expanded && tw`block`]}>
        <ul tw="font-domaine font-normal text-white col-span-3 text-[36px] mt-[30px]">
          <li tw="mb-4">
            <Menu as="div" className="relative inline-block text-left">
              <div>
                <Menu.Button className="inline-flex w-full justify-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset hover:bg-gray-50">
                  About
                  <FeatherIcon
                    icon="chevron down"
                    cssStyle={tw`inline w-8 mr-2`}
                  />
                </Menu.Button>
              </div>
              {/* Use the `Transition` component. */}
              <Transition
                enter="transition duration-100 ease-out"
                enterFrom="transform scale-95 opacity-0"
                enterTo="transform scale-100 opacity-100"
                leave="transition duration-75 ease-out"
                leaveFrom="transform scale-100 opacity-100"
                leaveTo="transform scale-95 opacity-0"
              >
                <Menu.Items>
                  <div className="py-1" tw="pl-4">
                    {aboutLinks.map((link) => (
                      /* Use the `active` state to conditionally style the active item. */
                      <Menu.Item
                        key={link.href}
                        tw={'block py-2 text-[24px]'}
                        onClick={toggleMobileNav}
                      >
                        <a href={link.href}>{link.label}</a>
                      </Menu.Item>
                    ))}
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>
          </li>
          <li tw="mb-4">
            <Menu as="div" className="relative inline-block text-left">
              <div>
                <Menu.Button className="inline-flex w-full justify-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset hover:bg-gray-50">
                  Partnerships
                  <FeatherIcon
                    icon="chevron down"
                    cssStyle={tw`inline w-8 mr-2`}
                  />
                </Menu.Button>
              </div>
              {/* Use the `Transition` component. */}
              <Transition
                enter="transition duration-100 ease-out"
                enterFrom="transform scale-95 opacity-0"
                enterTo="transform scale-100 opacity-100"
                leave="transition duration-75 ease-out"
                leaveFrom="transform scale-100 opacity-100"
                leaveTo="transform scale-95 opacity-0"
              >
                <Menu.Items>
                  <div className="py-1" tw="pl-4">
                    {partnerShipLinks.map((link) => (
                      /* Use the `active` state to conditionally style the active item. */
                      <Menu.Item
                        key={link.href}
                        tw={'block py-2 text-[24px]'}
                        onClick={toggleMobileNav}
                      >
                        <a href={link.href}>{link.label}</a>
                      </Menu.Item>
                    ))}
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>
          </li>
          <li tw="mb-4">
            <Link to="/#about" onClick={toggleMobileNav}>
              For Individuals
            </Link>
          </li>
          <li tw="mb-4">
            <a
              href="https://app.gabbi.com/login"
              target="_blank"
              rel="noopener noreferrer"
              onClick={toggleMobileNav}
            >
              Sign in
            </a>
          </li>
          <li tw="mb-4">
            <a
              href="https://app.gabbi.com/login"
              target="_blank"
              rel="noopener noreferrer"
              onClick={toggleMobileNav}
            >
              Partner with us
            </a>
          </li>
        </ul>
      </Grid>
    </section>
  )
}

export default observer(Header)
